import { Download, FolderInput, Trash2, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';

/**
 * The bulk-action bar for the Media Library grid, shown only while something is selected.
 *
 * Sticky at the top of the grid column rather than floating over the page: the actions stay
 * reachable while scrolling a long library, without covering the tiles being chosen. Clear
 * selection leads because it is the way out; Delete is last and the only destructive one.
 */
export function MediaBulkBar({
  count,
  selectableTotal,
  busy,
  downloading,
  onSelectAll,
  onClear,
  onMove,
  onDownload,
  onDelete,
}: {
  count: number;
  /**
   * How many of the visible items "Select all" can actually take — the grid length capped to what
   * one bulk request carries. Quoting the true number keeps the button from promising a selection
   * it will silently truncate.
   */
  selectableTotal: number;
  /** A bulk mutation or the ZIP build is in flight — the acting buttons are disabled meanwhile. */
  busy: boolean;
  /** The ZIP is being built and streamed; can take a while, so the button says so. */
  downloading: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  onMove: () => void;
  onDownload: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      role="region"
      aria-label="Bulk actions"
      // z-30 puts the bar above the cards scrolling under it (their checkboxes are z-10) while
      // staying below an open card menu at z-40, which the user has just deliberately opened.
      className="sticky top-0 z-30 flex flex-wrap items-center gap-2 rounded-xl border border-line bg-tint/95 px-3 py-2 shadow-sm backdrop-blur"
    >
      <span className="text-sm font-medium text-tint-fg">
        {count} selected
      </span>

      {selectableTotal > count && (
        <button
          type="button"
          onClick={onSelectAll}
          className="focus-ring rounded text-sm font-medium text-link hover:text-action-hover"
        >
          Select all {selectableTotal}
        </button>
      )}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onClear}>
          <X className="h-4 w-4" aria-hidden />
          Clear selection
        </Button>
        <Button variant="secondary" size="sm" disabled={busy} onClick={onMove}>
          <FolderInput className="h-4 w-4" aria-hidden />
          Move to folder
        </Button>
        <Button variant="secondary" size="sm" loading={downloading} disabled={busy} onClick={onDownload}>
          {!downloading && <Download className="h-4 w-4" aria-hidden />}
          {downloading ? 'Preparing ZIP…' : 'Download'}
        </Button>
        <Button variant="danger" size="sm" disabled={busy} onClick={onDelete}>
          <Trash2 className="h-4 w-4" aria-hidden />
          Delete
        </Button>
      </div>
    </div>
  );
}
